import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Waiting } from "./Shell";
import { SectionHead } from "./SectionHead";

// The Librarian — what arrived and who is named on it. Held & orphans is the landing surface (where
// deal@ lands); the filing pipeline shows documents in flight; held merges apply only after a plan
// preview (a wrong merge touches two buildings at once); the parties surface enforces the rule that
// sender identity never attaches an owner. Grouping rules live in the engine — this surfaces them.
export default function TheLibrarian() {
  const { authedFetch } = useAuth();
  const { waiting, loaded } = useContext(Waiting);
  const onYou = waiting["librarian"] || 0;
  const [tab, setTab] = useState("held");
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    authedFetch("/librarian/section").then(setV).catch((e) => setErr(String(e)));
  }, [authedFetch]);
  useEffect(load, [load]);

  if (err) return <div className="gk-err" data-testid="lib-error">{err}</div>;
  if (!v) return <div className="loading" data-testid="lib-loading">Loading…</div>;

  // F128 (founder ruling 30 Sep, built 11 Oct): the PARTIES tab is removed, not shown as empty —
  // `party` has no writer in R1, so the panel could only ever be an honest-looking zero.
  const counts = { held: v.held_and_orphans.count, filing: v.filing.rows.length,
                   merges: v.merges.count };
  const tabs = [["held", "Held & orphans"], ["filing", "Filing pipeline"],
                ["merges", "Merges"]];
  // The headline uses the SAME number as the rail's badge (founder ruling 30 Sep). The rest of the
  // queue is said in WORDS and never counted as a request: in dev that is the labelled fixtures
  // carrying the surface, plus whatever is merely in flight.
  const fixtures = v.held_and_orphans.items.filter((i) => i.fixture).length;
  const inFlight = counts.filing - (v.filing.counts?.filed || 0) - (v.filing.counts?.held || 0);
  const queueWords = [
    fixtures > 0 ? `${fixtures} labelled ${fixtures === 1 ? "fixture" : "fixtures"} carry this surface in dev — never a request` : null,
    inFlight > 0 ? `${inFlight} filing now` : null,
    counts.merges > 0 ? `${counts.merges} held ${counts.merges === 1 ? "merge" : "merges"} in the Merges tab` : null,
  ].filter(Boolean).join(" · ") || null;

  return (
    <div data-testid="the-librarian">
      <SectionHead eyebrow="THE LIBRARIAN · WHAT IT COULD NOT FILE ALONE" testid="lib"
        headline={!loaded
          ? `${counts.held} in the held & orphans queue.`
          : onYou > 0
            ? `${onYou} ${onYou === 1 ? "document is" : "documents are"} held — each needs a building.`
            : "Nothing is waiting on you."}
        lede={queueWords}>
        <p className="gk-constraint" data-testid="lib-constraint">{v.constraint}</p>
      </SectionHead>
      <div role="tablist" className="tabs desk-tabs">
        {tabs.map(([id, label]) => (
          <button key={id} role="tab" className={`desk-tab ${tab === id ? "on" : ""}`}
                  data-testid={`lib-tab-${id}`} aria-selected={tab === id} onClick={() => setTab(id)}>
            {label}<span className="tab-count">{counts[id]}</span>
          </button>
        ))}
      </div>
      {tab === "held" && <HeldOrphans h={v.held_and_orphans} authedFetch={authedFetch} reload={load} />}
      {tab === "filing" && <Filing f={v.filing} />}
      {tab === "merges" && <Merges m={v.merges} authedFetch={authedFetch} reload={load} />}
    </div>
  );
}

function FixtureBanner() {
  return (
    <div className="gk-fixture-banner" data-testid="lib-fixture-banner">
      Showing labelled fixtures — dev review only, never promotable, production never seeded.
    </div>
  );
}

// F128: `SenderRule` went with the Parties panel — it was that panel's own note.
function HeldOrphans({ h, authedFetch, reload }) {
  return (
    <div className="gk-panel" data-testid="lib-held">
      {h.fixtures_included && <FixtureBanner />}
      <p className="gk-note">{h.note}</p>

      <div className="stat-row" data-testid="lib-held-by-kind">
        {Object.entries(h.by_kind).map(([kind, n]) => (
          <div className="stat" key={kind} data-testid={`lib-kind-${kind}`}>
            <div className="stat-val">{n}</div>
            <div className="stat-label">{h.kind_labels[kind] || kind}</div>
          </div>
        ))}
      </div>

      {h.items.length === 0
        ? <div className="empty-state"><p>Nothing held. When deal@ or an upload cannot be filed, it lands here.</p></div>
        : h.items.map((it) => (
            <HeldCard key={it.id} it={it} authedFetch={authedFetch} reload={reload} />))}

      <div className="gk-b9-block">
        <h3>deal@ intake <span className="gk-tag">dependencies, built elsewhere</span></h3>
        <ul className="gk-b9-list" data-testid="lib-deal-deps">
          {h.deal_at_dependencies.map((d) => (
            <li key={d.name}><strong>{d.name}</strong> — {d.note}</li>
          ))}
        </ul>
        <p className="gk-muted">deal@ converges on this same held queue when these land; the surface is
          useful now via console and admin upload.</p>
      </div>
    </div>
  );
}

function HeldCard({ it, authedFetch, reload }) {
  const actionable = it.kind === "orphan" || it.kind === "unrecognised_sender";
  return (
    <div className="held-card" data-testid={`lib-held-${it.id}`}>
      <div className="held-head">
        <span className="held-kind" data-testid={`lib-held-kind-${it.id}`}>{it.kind.replace(/_/g, " ")}</span>
        <span className="held-field"><strong>{it.title}</strong></span>
        <span className="gk-muted mono" style={{ marginLeft: "auto" }}>{it.source}</span>
      </div>
      <p className="held-why" data-testid={`lib-held-why-${it.id}`}>{it.held_reason}</p>
      <p className="gk-muted lib-resolves"><strong>Resolves with:</strong> {it.resolves_with}</p>
      {it.kind === "held_merge" && <p className="gk-muted lib-resolves">Review this in the Merges tab — it applies only after a plan preview.</p>}
      {actionable && <OrphanActions it={it} authedFetch={authedFetch} reload={reload} />}
    </div>
  );
}

const ORPHAN_ACTIONS = [
  ["leave_orphaned", "Leave orphaned"],
  ["attach_to_property", "Attach to a property"],
  ["mark_not_a_property", "Mark not-a-property"],
  ["request_clarification", "Request clarification"],
];

function OrphanActions({ it, authedFetch, reload }) {
  const [action, setAction] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [target, setTarget] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const needsTarget = action === "attach_to_property";
  const needsReason = action && action !== "leave_orphaned";

  async function submit() {
    setBusy(true); setMsg("");
    try {
      const r = await authedFetch("/librarian/orphan/resolve", { method: "POST",
        body: { orphan_id: it.id, action, reasoning, target_property_id: target || null } });
      setMsg(r.message + (r.persisted ? "" : ""));
      if (r.persisted) reload();
    } catch (e) { setMsg(String(e)); }
    setBusy(false);
  }

  return (
    <div className="lib-orphan-actions" data-testid={`lib-orphan-actions-${it.id}`}>
      <div className="fm-dial-set">
        {ORPHAN_ACTIONS.map(([id, label]) => (
          <button key={id} className={`fm-dial-opt ${action === id ? "on" : ""}`}
                  data-testid={`lib-orphan-opt-${it.id}-${id}`} onClick={() => { setAction(id); setMsg(""); }}>
            {label}
          </button>
        ))}
      </div>
      {action && (
        <div className="action-bar">
          {needsTarget && (
            <input className="intent-input" placeholder="target property id" value={target}
                   data-testid={`lib-orphan-target-${it.id}`} onChange={(e) => setTarget(e.target.value)} />
          )}
          {needsReason && (
            <input className="intent-input" placeholder="reason on the record" value={reasoning}
                   data-testid={`lib-orphan-reason-${it.id}`} onChange={(e) => setReasoning(e.target.value)} />
          )}
          <button className="act act-approve" data-testid={`lib-orphan-submit-${it.id}`}
                  disabled={busy} onClick={submit}>Apply</button>
        </div>
      )}
      {needsTarget && (
        <p className="gk-muted" data-testid={`lib-attach-note-${it.id}`}>
          Attaching is recorded as <strong>stated</strong>, never promoted to verified — and never made on
          the strength of the sender.
        </p>
      )}
      {msg && <div className="gk-msg" data-testid={`lib-orphan-msg-${it.id}`}>{msg}</div>}
    </div>
  );
}

function Filing({ f }) {
  return (
    <div className="gk-panel" data-testid="lib-filing">
      {f.fixtures_included && <FixtureBanner />}
      <p className="gk-note">{f.note}</p>
      <div className="stat-row" data-testid="lib-filing-counts">
        {f.states.map((s) => (
          <div className="stat" key={s} data-testid={`lib-filing-state-${s}`}>
            <div className="stat-val">{f.counts[s] || 0}</div>
            <div className="stat-label">{s}</div>
          </div>
        ))}
      </div>
      {f.rows.length === 0
        ? <div className="empty-state"><p>No documents in flight. In Release 1 there is no live intake yet.</p></div>
        : (
          <table className="gk-table" data-testid="lib-filing-table">
            <thead><tr><th>document</th><th>state</th><th>arrived</th><th>note</th></tr></thead>
            <tbody>
              {f.rows.map((r) => (
                <tr key={r.id} data-testid={`lib-file-${r.id}`}>
                  <td className="mono">{r.name}</td>
                  <td><span className={`status-pill ${r.filing_state === "held" ? "sp-needs-human" : r.filing_state === "failed" ? "sp-rejected" : "sp-unscored"}`}>{r.filing_state}</span></td>
                  <td>{r.arrival_at ? String(r.arrival_at).slice(0, 10) : "—"}</td>
                  <td className="reason-cell">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

function Merges({ m, authedFetch, reload }) {
  return (
    <div className="gk-panel" data-testid="lib-merges">
      {m.fixtures_included && <FixtureBanner />}
      <p className="gk-note">{m.note}</p>
      {m.merges.length === 0
        ? <div className="empty-state"><p>No held merges.</p></div>
        : m.merges.map((mg) => <MergeCard key={mg.id} mg={mg} authedFetch={authedFetch} reload={reload} />)}
    </div>
  );
}

function MergeCard({ mg, authedFetch, reload }) {
  const [plan, setPlan] = useState(null);
  const [reasoning, setReasoning] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  async function preview() {
    setMsg("");
    try { setPlan(await authedFetch("/librarian/merge/preview", { method: "POST", body: { merge_id: mg.id } })); }
    catch (e) { setMsg(String(e)); }
  }
  async function proceed() {
    setBusy(true); setMsg("");
    try {
      const r = await authedFetch("/librarian/merge/apply", { method: "POST",
        body: { merge_id: mg.id, reasoning } });
      setMsg(r.message);
      if (r.persisted) reload(); else { setPlan(null); }
    } catch (e) { setMsg(String(e)); }
    setBusy(false);
  }

  const sig = mg.corroborating_signal;
  return (
    <div className="held-card" data-testid={`lib-merge-${mg.id}`}>
      <div className="held-head">
        <span className="status-pill sp-unscored">held merge</span>
        <span className="held-field"><strong>{mg.members.map((x) => x.property_name).join(" · ")}</strong></span>
      </div>
      <table className="gk-table" data-testid={`lib-merge-members-${mg.id}`}>
        <thead><tr><th>doc</th><th>property name</th><th>address</th><th>apn</th><th>owner</th><th>brokerage</th></tr></thead>
        <tbody>
          {mg.members.map((x) => (
            <tr key={x.doc}>
              <td className="mono">{x.doc}</td>
              <td>{x.property_name || <span className="gk-muted">—</span>}</td>
              <td>{x.address || <span className="gk-muted">not present</span>}</td>
              <td>{x.apn || <span className="gk-muted">not present</span>}</td>
              <td>{x.owner_entity || <span className="gk-muted">not present</span>}</td>
              <td>{x.brokerage || <span className="gk-muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="held-why">
        {sig.present
          ? <>Corroborating signal: <strong>{sig.kind}</strong> — <span className="mono">{sig.value}</span>.</>
          : <span className="gk-warn-text">No corroborating locational signal (address / APN / legal).</span>}
      </p>
      {mg.entity_name_only.insufficient && (
        <p className="gk-muted" data-testid={`lib-merge-entityonly-${mg.id}`}>
          Shared entity name <strong>{mg.entity_name_only.name}</strong> ({mg.entity_name_only.kind}) is
          <strong> insufficient</strong> — {mg.entity_name_only.reason}
        </p>
      )}
      <p className="gk-muted">{mg.why_held}</p>

      {!plan
        ? <button className="act" data-testid={`lib-merge-preview-${mg.id}`} onClick={preview}>Preview merge</button>
        : (
          <div className="st-edit-box st-preview" data-testid={`lib-merge-plan-${mg.id}`}>
            <span className="st-diff">{plan.members.map((x) => x.doc).join(" + ")} <strong>→</strong> one property</span>
            {plan.can_apply
              ? <>
                  <input className="intent-input" placeholder="reason on the record" value={reasoning}
                         data-testid={`lib-merge-reason-${mg.id}`} onChange={(e) => setReasoning(e.target.value)} />
                  <button className="act act-approve" data-testid={`lib-merge-proceed-${mg.id}`}
                          disabled={busy} onClick={proceed}>Proceed</button>
                </>
              : <span className="gk-warn-text" data-testid={`lib-merge-blocked-${mg.id}`}>{plan.blocked_reason}</span>}
            <button className="linkbtn" data-testid={`lib-merge-cancel-${mg.id}`} onClick={() => setPlan(null)}>Cancel</button>
          </div>
        )}
      {msg && <div className="gk-msg" data-testid={`lib-merge-msg-${mg.id}`}>{msg}</div>}
    </div>
  );
}

// F128: the `Parties` panel is removed with its tab — no writer, so no surface.
